// consolidated slice actions for request, response, and query parameter schema editors

import type { APIDesignerStore } from '..';
import type { WritableDraft } from 'immer/dist/internal.js';
import type { ResponseObject, MediaTypeObject } from 'oas/types';
import type { OpenAPIV3 } from 'openapi-types';
import type { StateCreator } from 'zustand';

import { codes } from '@readme/http-status-codes';

import { actionLog } from '@core/store/util';

import { getValidatedAPIDesignerState } from '../util';

import {
  MIMEType,
  type MIMETypeValue,
  type UpdateSchemaAttrObject,
  type SchemaEditorTarget,
  type ValidResponseSchema,
  getRequestSchema,
  getResponseSchema,
  validateStatusCode,
  validatePropertyPath,
  updateRecentlyUsedMediaTypes,
  handleRequestMediaTypeChange,
  handleResponseMediaTypeChange,
  createEmptySchema,
  executeSchemaAction,
  addPropertyToRequestRoot,
  addPropertyToResponseRoot,
  addPropertyToQueryParam,
  getSchemaRootType,
  isPolymorphicSchema,
  transformPolymorphicToSingle,
  transformPolymorphicToPolymorphic,
  transformSingleToPolymorphic,
  transformObjectArray,
} from './utils';

export interface BodySchemaEditorSliceActions {
  /**
   * Adds a new polymorphic schema to the request or response body schema of the current endpoint.
   */
  addPolymorphicSchema: (
    args: { target: 'request'; mimeType: string } | { target: 'response'; mimeType: string; statusCode: string },
  ) => void;

  /**
   * Adds a new property to the request or response body schema of the current endpoint.
   *
   * The first element is always 0, which represents the top-level object.
   * If the id is "0", it means the property should be added to root level of the schema object.
   * If the id is "0-1-2", it means the property should be added to the third property of the second property of the root schema object.
   */
  addProperty: (args: {
    /**
     * The type of the property to add - 'request' or 'response' or a 'query' object with its location info
     */
    target: SchemaEditorTarget;
    /**
     * The id of the property to add. This is a string that represents the path to the property in the schema.
     */
    id: string;
    /**
     * The HTTP status code of the response. Only used for response bodies.
     */
    statusCode?: string;
    /**
     * The MIME type of the property to add. Only used for response bodies.
     */
    mimeType?: MIMETypeValue;
    /**
     * The index of the polymorphic schema to add.
     * Polymorphic schemas are arrays, basically OpenAPIV3.SchemaObject[]
     * we use this index to identify which of the schemas should be modified
     */
    childPolymorphicIndex?: number;
  }) => void;

  /**
   * Adds a new response schema to the current endpoint.
   */
  addResponseSchema: (
    /**
     * The HTTP status code of the response
     */
    statusCode: string,
    /**
     * The MIME type of the response schema to add. Defaults to 'application/json'.
     * Note: we want to use string here instead of MIMETypeValue because customers may define custom media types
     */
    mimeType?: string,
  ) => void;

  /**
   * Allow the old root type to be any string
   * The end result will still be anyOf, object, or oneOf
   */
  changePolymorphicSchemaRootType: (
    args:
      | {
          target: 'request';
          oldRootType: string;
          newRootType: 'anyOf' | 'array' | 'array(primitive)' | 'object' | 'oneOf';
          mimeType: string;
        }
      | {
          target: 'response';
          oldRootType: string;
          newRootType: 'anyOf' | 'array' | 'array(primitive)' | 'object' | 'oneOf';
          mimeType: string;
          statusCode: string;
        },
  ) => void;

  /**
   * Changes the status code of a response schema.
   */
  changeResponseStatusCode: (
    /**
     * The HTTP status code of the response
     */
    statusCode: string,
    /**
     * The new HTTP status code of the response
     */
    newStatusCode: string,
  ) => void;

  /**
   * Changes the media type of a request or response schema.
   * Note: we want to use string here instead of MIMETypeValue because customers may define custom media types
   */
  changeSchemaMediaType: (
    args:
      | { target: 'request'; id: string; oldMimeType: string; newMimeType: string }
      | { target: 'response'; id: string; statusCode: string; oldMimeType: string; newMimeType: string },
  ) => void;

  /**
   * Deletes a polymorphic schema from the request or response body schema of the current endpoint.
   */
  deletePolymorphicSchema: (
    args:
      | { target: 'request'; mimeType: string; index: number }
      | { target: 'response'; mimeType: string; statusCode: string; index: number },
  ) => void;

  /**
   * Gets all the status codes of response schemas for the current endpoint.
   *
   * @returns An array of status codes.
   */
  getAllResponseStatusCodes: () => string[];

  /**
   * Gets the existing media types for a response schema.
   *
   * @returns An array of media types.
   */
  getExistingMediaTypes: (
    /**
     * The HTTP status code of the response
     */
    statusCode: string,
  ) => MIMETypeValue[];

  /**
   * Gets the valid response schemas for the current endpoint with proper filtering and formatting.
   * By default, it looks for 'application/json' MIME types, but in the case where it doesn't find any, it will
   * return the first schema it finds, regardless of MIME type.
   *
   * Can be toggled to allow returning multiple schemas with different MIME types.
   *
   * If there is no schema AT ALL, it will return an empty array.
   * We should never modify the return value and must always stay true to the original schema/OAS file.
   * Use that OAS file as a single source of truth.
   *
   * @returns An array of response schemas with their status codes, descriptions, and MIME type schemas.
   */
  getValidResponseSchemas: () => ValidResponseSchema[];

  /**
   * Initializes an empty request or response body for the current endpoint with the specified status code
   * Note: This function only works with request and response bodies, not query parameters.
   */
  init: (
    /**
     * The target of the body to initialize - 'request' or 'response'
     */
    target: 'request' | 'response',
    /**
     * The HTTP status code of the response. Only used for response bodies.
     */
    statusCode?: string,
  ) => void;

  /**
   * Removes a property from the request body, response body, or query parameter schema of the current endpoint.
   */
  removeProperty: (args: {
    /**
     * The type of the property to remove - 'request' or 'response' or a 'query' object with its location info
     */
    target: SchemaEditorTarget;
    /**
     * The id of the property to remove. This is a string that represents the path to the property in the schema.
     */
    id: string;
    /**
     * The HTTP status code of the response. Only used for response bodies.
     */
    statusCode?: string;
    /**
     * The MIME type of the property to remove. Only used for response bodies.
     */
    mimeType?: MIMETypeValue;
    /**
     * The index of the polymorphic schema to remove.
     * Polymorphic schemas are arrays, basically OpenAPIV3.SchemaObject[]
     * we use this index to identify which of the schemas should be modified
     */
    childPolymorphicIndex?: number;
  }) => void;

  /**
   * Removes a response schema from the current endpoint.
   */
  removeResponseSchema: (
    /**
     * The HTTP status code of the response
     */
    statusCode: string,
  ) => void;

  /**
   * Updates the description of a response schema.
   */
  updateResponseSchemaDescription: (args: {
    /**
     * The HTTP status code of the response
     */
    statusCode: string;
    /**
     * The id of the property to update the description for. This is a string that represents the path to the property in the schema.
     */
    id: string;
    /**
     * The description to set for the property
     */
    description: string;
    /**
     * The MIME type of the property to update the description for. Only used for response bodies.
     */
    mimeType?: MIMETypeValue;
  }) => void;

  /**
   * Updates the schema attribute of a request body, response body, or query parameter.
   */
  updateSchemaAttribute: (args: {
    /**
     * The type of the schema to update - 'request' or 'response' or a 'query' object with its location info
     */
    target: SchemaEditorTarget;
    /**
     * The id of the property to update. This is a string that represents the path to the property in the schema.
     */
    id: string;
    /**
     * The object containing the attribute to update and its value.
     */
    updateObject: UpdateSchemaAttrObject;
    /**
     * The HTTP status code of the response. Only used for response bodies.
     */
    statusCode?: string;
    /**
     * The MIME type of the property to update. Only used for response bodies.
     */
    mimeType?: MIMETypeValue;
    /**
     * The index of the polymorphic schema to update.
     * Polymorphic schemas are arrays, basically OpenAPIV3.SchemaObject[]
     * we use this index to identify which of the schemas should be modified
     */
    childPolymorphicIndex?: number;
  }) => void;

  /**
   * Updates the title of a request or response body schema.
   */
  updateSchemaTitle: (
    args:
      | {
          target: 'request';
          existingSchemaProperties: {
            existingTitle: string;
            existingMimeType: string; // to allow custom media types
            existingIndex: number;
          };
          newTitle: string;
        }
      | {
          target: 'response';
          existingSchemaProperties: {
            existingTitle: string;
            existingMimeType: string; // to allow custom media types
            existingIndex: number;
          };
          newTitle: string;
          statusCode: string;
        },
  ) => void;
}

export interface BodySchemaEditorSlice {
  bodySchemaEditor: BodySchemaEditorSliceActions;
}

export const createBodySchemaEditorSlice: StateCreator<
  APIDesignerStore & BodySchemaEditorSlice,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  BodySchemaEditorSlice
> = (set, get) => ({
  bodySchemaEditor: {
    // unified property management functions
    addProperty: ({ target, id, statusCode, mimeType, childPolymorphicIndex }) => {
      validateStatusCode(statusCode || '');
      validatePropertyPath(id);

      set(
        state => {
          /**
           * case 1: target request at root
           * case 1.1: target request at root, with existing content
           * case 1.1.1: target request at root, with existing content and polymorphic index
           * case 1.1.2: target request at root, with existing content and no polymorphic index
           * case 1.2: target request at root, with no existing content
           * case 2: target response at root
           * case 2.1: target response at root, with existing content
           * case 2.1.1: target response at root, with existing content and polymorphic index
           * case 2.1.2: target response at root, with existing content and no polymorphic index
           * case 2.2: target response at root, with no existing content
           * case 3: target request but not at root
           * case 4: target response but not at root
           */
          const { operation } = getValidatedAPIDesignerState(state);
          const effectiveMimeType = mimeType || MIMEType.ApplicationJSON;

          if (id === '0') {
            // Add properties at root level
            if (target === 'request') {
              // case 1: target request at root (handles 1.1, 1.1.1, 1.1.2, 1.2)
              addPropertyToRequestRoot(operation, effectiveMimeType, childPolymorphicIndex);
            } else if (target === 'response' && statusCode) {
              // case 2: target response at root (handles 2.1, 2.1.1, 2.1.2, 2.2)
              addPropertyToResponseRoot(operation, statusCode, effectiveMimeType, childPolymorphicIndex);
            } else if (typeof target === 'object' && target.target === 'query') {
              // query parameter case
              addPropertyToQueryParam(state, target);
            }
          } else {
            // case 3 & 4: target request/response but not at root
            executeSchemaAction({
              target,
              id,
              state,
              action: { action: 'add' },
              statusCode,
              mimeType: effectiveMimeType,
              childPolymorphicIndex,
            });
          }
        },
        false,
        actionLog('bodySchemaEditor.addProperty', { target, id, statusCode }),
      );
    },

    removeProperty: ({ target, id, statusCode, mimeType, childPolymorphicIndex }) => {
      validateStatusCode(statusCode || '');
      validatePropertyPath(id);

      set(
        state => {
          const { operation } = getValidatedAPIDesignerState(state);

          /**
           * Handle root-level removals differently from property-level removals:
           * - Root (id='0'): Delete entire request/response body or specific media type
           * - Property: Use traversal helper to remove nested properties
           */
          if (id === '0') {
            if (target === 'request') {
              // remove request body at root level
              const currentRequestBody = operation.requestBody;
              if (currentRequestBody && 'content' in currentRequestBody && currentRequestBody.content) {
                if (mimeType) {
                  // remove specific media type
                  delete currentRequestBody.content[mimeType];

                  // delete entire requestBody if no media types remain
                  if (Object.keys(currentRequestBody.content).length === 0) {
                    delete operation.requestBody;
                  }
                } else {
                  // delete entire requestBody
                  delete operation.requestBody;
                }
              }
            } else if (target === 'response' && statusCode) {
              // remove response body at root level
              if (operation.responses?.[statusCode] && 'content' in operation.responses[statusCode]) {
                if (mimeType) {
                  delete operation.responses[statusCode]?.content?.[mimeType];
                  // keep status code with description and other properties
                } else {
                  delete operation.responses[statusCode];
                }
              }
            }
          } else {
            // remove nested properties using traversal helper
            executeSchemaAction({
              target,
              id,
              state,
              action: { action: 'remove' },
              statusCode,
              mimeType,
              childPolymorphicIndex,
            });
          }
        },
        false,
        actionLog('bodySchemaEditor.removeProperty', { target, id, statusCode }),
      );
    },

    updateSchemaAttribute: ({ target, id, updateObject, statusCode, mimeType, childPolymorphicIndex }) => {
      set(
        state => {
          getValidatedAPIDesignerState(state);

          if (id === '0') {
            // this should never be hit since the root object doesn't have attributes like this (yet!)
          } else {
            executeSchemaAction({
              target,
              id,
              state,
              action: { action: 'update', ...updateObject },
              statusCode,
              mimeType,
              childPolymorphicIndex,
            });
          }
        },
        false,
        actionLog('bodySchemaEditor.updateSchemaAttribute', { target, updateObject, id, statusCode }),
      );
    },

    changeSchemaMediaType: args => {
      set(
        state => {
          const { operation } = getValidatedAPIDesignerState(state);
          if (args.target === 'request') {
            handleRequestMediaTypeChange(operation, args);
            updateRecentlyUsedMediaTypes(args.newMimeType, state);
          } else if (args.target === 'response') {
            handleResponseMediaTypeChange(operation, args);
            updateRecentlyUsedMediaTypes(args.newMimeType, state);
          }
        },
        false,
        actionLog('bodySchemaEditor.changeSchemaMediaType', args),
      );
    },

    // unified initialization function
    init: (target, statusCode) => {
      const newSchema = createEmptySchema(true);
      set(
        state => {
          const { operation } = getValidatedAPIDesignerState(state);

          if (target === 'request') {
            operation.requestBody = {
              content: {
                'application/json': { schema: newSchema },
              },
            };
          } else if (target === 'response' && statusCode) {
            operation.responses[statusCode] = {
              description: '',
              content: {
                'application/json': { schema: newSchema },
              },
            };
          }
        },
        false,
        actionLog('bodySchemaEditor.initBody', { target, statusCode }),
      );
    },

    // response-specific functions (can't be easily combined)
    addResponseSchema: (statusCode, mimeType) => {
      validateStatusCode(statusCode || '');

      set(
        state => {
          const { operation } = getValidatedAPIDesignerState(state);

          const currentResponse = operation.responses[statusCode];
          const targetMimeType = mimeType || MIMEType.ApplicationJSON;

          if (currentResponse && 'content' in currentResponse && currentResponse.content?.[targetMimeType]) {
            // we dont want to overwrite an existing schema with a new one, so we skip
            if (!currentResponse.content?.[targetMimeType]?.schema) {
              (operation.responses[statusCode] as WritableDraft<OpenAPIV3.ResponseObject>).content![
                targetMimeType
              ].schema = createEmptySchema(true);
            }
            return;
          }

          const description =
            currentResponse && 'description' in currentResponse
              ? (currentResponse.description as string)
              : codes[statusCode][0] || '';
          const content =
            currentResponse && 'content' in currentResponse
              ? (currentResponse.content as Record<string, MediaTypeObject>)
              : {};

          const newResponse = {
            description,
            content: {
              ...content,
              [targetMimeType]: {
                schema: createEmptySchema(true),
              },
            },
          };

          // add a new response schema for the given status code, defaults to empty object
          operation.responses[statusCode] = newResponse as WritableDraft<OpenAPIV3.ResponseObject>;
          updateRecentlyUsedMediaTypes(targetMimeType, state);
        },
        false,
        actionLog('bodySchemaEditor.addResponseSchema', { statusCode }),
      );
    },

    removeResponseSchema: statusCode => {
      set(
        state => {
          const { operation } = getValidatedAPIDesignerState(state);
          delete operation.responses[statusCode];
        },
        false,
        actionLog('bodySchemaEditor.removeResponseSchema', { statusCode }),
      );
    },

    changeResponseStatusCode: (statusCode, newStatusCode) => {
      set(
        state => {
          const { operation } = getValidatedAPIDesignerState(state);
          const responseData = operation.responses[statusCode];

          // Check if responseData has a description property (is a ResponseObject)
          if ('description' in responseData && responseData.description) {
            const currentDescription = responseData.description;

            // if the description is default, update it to the new status code description
            // if the description is not default, do not update it
            if (currentDescription.trim().toLowerCase() === codes[statusCode][0].trim().toLowerCase()) {
              responseData.description = codes[newStatusCode][0];
            }
          }

          if (!responseData) {
            throw new Error(`Response with status code ${statusCode} not found`);
          }

          // Move response data from old status code to new status code
          operation.responses[newStatusCode] = responseData;
          delete operation.responses[statusCode];
        },
        false,
        actionLog('bodySchemaEditor.changeResponseStatusCode', { statusCode, newStatusCode }),
      );
    },

    getValidResponseSchemas: () => {
      const { operation } = getValidatedAPIDesignerState(get());
      const responsesArray = Object.keys(operation?.responses || {}).map(statusCode => {
        return {
          statusCode,
          response: operation?.responses?.[statusCode] as ResponseObject,
        };
      });

      const schemas: {
        statusCode: string;
        description: string;
        schemas: {
          type: MIMETypeValue;
          schema: OpenAPIV3.SchemaObject;
        }[];
      }[] = responsesArray.map(({ statusCode, response }) => {
        const responseSchemas = Object.keys(response.content || {});

        /**
         * we should never in actuality have a schema with a ref, this filtering will be handled by the parent component
         * we also do not accept responses with no schemas
         *
         * ie. responses with only examples will still be flagged as invalid and not returned
         */
        const validResponseSchemas = responseSchemas.filter(mimeType => {
          const schema = response.content?.[mimeType]?.schema;
          if (!schema) return true;
          return !('$ref' in schema);
        });

        const responseSchemasArray: { type: MIMETypeValue; schema: OpenAPIV3.SchemaObject }[] = [];

        // Sort MIME types: JSON first, then alphabetical
        const sortedMimeTypes = validResponseSchemas.sort((a, b) => {
          if (a === 'application/json') return -1;
          if (b === 'application/json') return 1;
          return a.localeCompare(b);
        });

        sortedMimeTypes.forEach(mimeType => {
          const schema = response.content?.[mimeType]?.schema;
          responseSchemasArray.push({ type: mimeType as MIMETypeValue, schema: schema as OpenAPIV3.SchemaObject });
        });

        return {
          statusCode,
          description: response.description,
          schemas: responseSchemasArray,
        };
      });

      return schemas;
    },

    updateResponseSchemaDescription: ({ statusCode, id, description, mimeType }) => {
      set(
        state => {
          const { operation } = getValidatedAPIDesignerState(state);
          const response = operation.responses[statusCode];
          if (!response) {
            throw new Error(`Response with status code ${statusCode} not found`);
          }

          if (id === '0') {
            // update the response description for the root level
            if ('description' in response) {
              (response as WritableDraft<OpenAPIV3.ResponseObject>).description = description;
            }
          } else {
            // update description for nested schema properties
            executeSchemaAction({
              target: 'response',
              id,
              state,
              action: { action: 'update', attribute: 'description', value: description },
              statusCode,
              mimeType: mimeType || MIMEType.ApplicationJSON,
            });
          }
        },
        false,
        actionLog('bodySchemaEditor.updateResponseSchemaDescription', { statusCode, id, description, mimeType }),
      );
    },

    getAllResponseStatusCodes: () => {
      const { operation } = getValidatedAPIDesignerState(get());

      return Object.keys(operation.responses || {});
    },

    getExistingMediaTypes: (statusCode: string) => {
      const { operation } = getValidatedAPIDesignerState(get());

      const response = operation.responses?.[statusCode] as ResponseObject;
      if (!response) {
        throw new Error(`Response with status code ${statusCode} not found`);
      }

      return Object.keys(response.content || {}).map(mimeType => mimeType as MIMETypeValue);
    },

    // polymorphic schema functions
    // only supports top level schema titles in the mean time
    updateSchemaTitle: args => {
      const { target, existingSchemaProperties, newTitle } = args;
      const statusCode = 'statusCode' in args ? args.statusCode : '';

      set(state => {
        const schema =
          target === 'request'
            ? getRequestSchema(state, existingSchemaProperties.existingMimeType)
            : getResponseSchema(state, statusCode, existingSchemaProperties.existingMimeType);

        const polyContent = schema.anyOf || schema.oneOf;
        if (!polyContent) throw new Error('No polymorphic content found');

        if (
          existingSchemaProperties.existingIndex < 0 ||
          existingSchemaProperties.existingIndex >= polyContent.length
        ) {
          throw new Error('Index out of bounds');
        }

        const targetSchema = polyContent[existingSchemaProperties.existingIndex] as OpenAPIV3.SchemaObject;
        if (!targetSchema) throw new Error('Target schema not found');

        targetSchema.title = newTitle.trim();
        return state;
      });
    },

    deletePolymorphicSchema: args => {
      const { target, index, mimeType } = args;
      const statusCode = 'statusCode' in args ? args.statusCode : '';

      set(state => {
        const schema =
          target === 'request' ? getRequestSchema(state, mimeType) : getResponseSchema(state, statusCode, mimeType);

        const polyContent = schema.anyOf || schema.oneOf;
        if (!polyContent) throw new Error('No polymorphic content found');

        if (index < 0 || index >= polyContent.length) {
          throw new Error('Index out of bounds');
        }

        polyContent.splice(index, 1);
        return state;
      });
    },

    addPolymorphicSchema: args => {
      const { target, mimeType } = args;
      const statusCode = 'statusCode' in args ? args.statusCode : '';

      set(state => {
        const schema =
          target === 'request' ? getRequestSchema(state, mimeType) : getResponseSchema(state, statusCode, mimeType);

        const polyContent = schema.anyOf || schema.oneOf;
        if (!polyContent) throw new Error('No polymorphic content found');

        polyContent.push(createEmptySchema(true));
        return state;
      });
    },

    changePolymorphicSchemaRootType: args => {
      const { target, oldRootType, newRootType, mimeType } = args;
      const statusCode = 'statusCode' in args ? args.statusCode : '';

      set(state => {
        const schema =
          target === 'request' ? getRequestSchema(state, mimeType) : getResponseSchema(state, statusCode, mimeType);

        const currentRootType = getSchemaRootType(schema);
        const isCurrentlyPolymorphic = isPolymorphicSchema(schema);
        const isNewPolymorphic = newRootType === 'anyOf' || newRootType === 'oneOf';

        /**
         * NOTE: we treat arrays as polymorphic schemas since their behavior is similar to polymorphic schemas
         *
         * case 1: polymorphic schema -> object
         * case 2: polymorphic schema -> array
         * case 3: polymorphic schema -> polymorphic schema
         * case 4: non-polymorphic schema -> polymorphic schema
         * case 4.1: object -> polymorphic schema
         * case 4.2: array -> polymorphic schema
         * case 5: object -> array
         * case 6: array -> object
         */

        if (isCurrentlyPolymorphic && !isNewPolymorphic) {
          // cases 1 & 2: polymorphic schema -> object/array
          if (
            (oldRootType === 'anyOf' || oldRootType === 'oneOf') &&
            (newRootType === 'array' || newRootType === 'array(primitive)' || newRootType === 'object')
          ) {
            transformPolymorphicToSingle(schema, oldRootType, newRootType);
          }
        } else if (isCurrentlyPolymorphic && isNewPolymorphic) {
          // case 3: polymorphic schema -> polymorphic schema
          if (
            (oldRootType === 'anyOf' || oldRootType === 'oneOf') &&
            (newRootType === 'anyOf' || newRootType === 'oneOf')
          ) {
            transformPolymorphicToPolymorphic(schema, oldRootType, newRootType);
          }
        } else if (!isCurrentlyPolymorphic && isNewPolymorphic) {
          // cases 4.1 & 4.2: object/array -> polymorphic schema
          if (
            (currentRootType === 'array' || currentRootType === 'array(primitive)' || currentRootType === 'object') &&
            (newRootType === 'anyOf' || newRootType === 'oneOf')
          ) {
            transformSingleToPolymorphic(schema, currentRootType, newRootType);
          }
        } else if (newRootType === 'array' || newRootType === 'array(primitive)' || newRootType === 'object') {
          // cases 5 & 6: object <-> array
          transformObjectArray(schema, newRootType);
        }

        return state;
      });
    },
  },
});
